import React, { useState, useEffect, useCallback, useMemo, useContext, createContext, useRef } from 'react';
import type { AxiosInstance } from 'axios';
import axios from 'axios';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient } from '../services/api';
import { useRefresh } from '../hooks/useRefresh';
import { RefreshButton } from '../components/RefreshButton';
import EmailMonitoringPage from './EmailMonitoringPage';
import AdminComplianceDocumentsPage from './AdminComplianceDocumentsPage';
import PaymentVerificationQueuePage from './PaymentVerificationQueuePage';

// ====================================================================
//  AdminUserManagementPage
//  For Admin and Ops roles.
//  Tabs: Users | Verification & Docs | Payments | Ratings | Mailbox | Credit | Stats | Errors
// ====================================================================

type Tab = 'users' | 'deleted' | 'verification' | 'payments' | 'ratings' | 'mailbox' | 'credit' | 'stats' | 'errors';

interface UserSummary {
  userId: string; email: string; fullName: string; roleName: string;
  registrationStatus: string; isEmailVerified: boolean; createdAt: string;
}

interface PendingVerification {
  userId: string; email: string; fullName: string; roleName: string;
  registrationStatus: string; carrierVerStatus?: string;
  garageVerStatus?: string; workshopVerStatus?: string; pendingDocuments: number; createdAt: string;
}

interface DvlaPendingItem {
  userId: string; carrierFullName: string; email: string;
  shareCode: string; submittedAt: string;
}

interface DvlaProcessResult {
  parseSuccess: boolean; parseFailureReason: string | null;
  carrierFullName: string | null; licenceNumber: string | null;
  drivingStatus: string | null; licenceValidTo: string | null;
  endorsementPoints: number;
}

interface PendingScreenshot {
  uploadId: string; platform: string; userRole: string; originalFileName: string;
  status: string; uploadedAt: string; screenshotSasUrl: string | null;
  fullName?: string;
}

interface AdminStats {
  totalUsers: number; pendingVerifications: number; pendingExternalRatings: number;
  activeJobsToday: number; completedJobsThisMonth: number; revenueThisMonth: number;
  openDisputes: number; registeredCarriers: number; registeredWorkshops?: number;
}

interface TechnicalError {
  id: number; errorType: string; message: string; stackTrace?: string;
  componentStack?: string; url?: string; userId?: string; userEmail?: string;
  appVersion?: string; platform?: string; userAgent?: string; context?: string;
  status: string; createdAt: string; resolvedAt?: string; resolutionNotes?: string;
  totalCount: number;
}

// ── Authenticated API context ─────────────────────────────────────────
// Tabs read from this context instead of a module-level stub so they
// always use the signed-in admin's real MSAL token.
const AdminApiContext = createContext<AxiosInstance>(axios.create());
const useAdminApi = () => useContext(AdminApiContext);

export default function AdminUserManagementPage() {
  const [tab, setTab]               = useState<Tab>('users');
  const [refreshTick, setRefreshTick] = useState(0);
  const { getToken } = useAppAuth();

  const adminApi = useMemo(() => createApiClient(getToken), [getToken]);

  const { isRefreshing, lastRefreshed, refresh } = useRefresh(
    useCallback(() => { setRefreshTick(t => t + 1); }, [])
  );

  return (
    <AdminApiContext.Provider value={adminApi}>
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.h1}>Admin Console</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <RefreshButton onRefresh={refresh} isRefreshing={isRefreshing} lastRefreshed={lastRefreshed} />
          <span style={styles.badge}>TransitDeal Operations</span>
        </div>
      </div>
      <div style={styles.tabs}>
        {(['users', 'deleted', 'verification', 'payments', 'ratings', 'mailbox', 'credit', 'stats', 'errors'] as Tab[]).map(t => (
          <button key={t} style={{ ...styles.tab, ...(tab === t ? styles.tabActive : {}) }}
            onClick={() => setTab(t)}>
            {tabLabel(t)}
          </button>
        ))}
      </div>
      <div style={styles.content}>
        {tab === 'users'        && <UsersTab              key={refreshTick} />}
        {tab === 'deleted'      && <DeletedAccountsTab    key={refreshTick} />}
        {tab === 'verification' && <VerificationTab       key={refreshTick} />}
        {tab === 'payments'     && <PaymentsTab           key={refreshTick} />}
        {tab === 'ratings'      && <ExternalRatingsTab    key={refreshTick} />}
        {tab === 'mailbox'      && <EmailMonitoringPage />}
        {tab === 'credit'       && <CreditRequestsTab     key={refreshTick} />}
        {tab === 'stats'        && <StatsTab              key={refreshTick} />}
        {tab === 'errors'       && <ErrorsTab             key={refreshTick} />}
      </div>
    </div>
    </AdminApiContext.Provider>
  );
}

// ── Create Ops User Panel ─────────────────────────────────────────────
interface CreateOpsForm { firstName: string; lastName: string; email: string; phoneNumber: string; }
interface CreateOpsResult { user: UserSummary; temporaryPassword: string; }

function CreateOpsPanel({ onCreated }: { onCreated: () => void }) {
  const api = useAdminApi();
  const [open, setOpen]     = useState(false);
  const [form, setForm]     = useState<CreateOpsForm>({ firstName: '', lastName: '', email: '', phoneNumber: '' });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<CreateOpsResult | null>(null);
  const [error, setError]   = useState<string | null>(null);

  const set = (field: keyof CreateOpsForm, value: string) =>
    setForm(f => ({ ...f, [field]: value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError(null); setResult(null);
    try {
      const res = await api.post<CreateOpsResult>('/api/v1/users/ops', {
        firstName:   form.firstName,
        lastName:    form.lastName,
        email:       form.email,
        phoneNumber: form.phoneNumber || undefined,
      });
      setResult(res.data);
      setForm({ firstName: '', lastName: '', email: '', phoneNumber: '' });
      onCreated();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? 'Failed to create Ops user. Check the email is not already in use.');
    } finally { setSaving(false); }
  };

  const dismiss = () => { setResult(null); setOpen(false); };

  return (
    <div style={{ marginBottom: 16 }}>
      {!open ? (
        <button style={styles.btnPrimary} onClick={() => setOpen(true)}>
          + Create Ops User
        </button>
      ) : (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, background: '#f9fafb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <strong style={{ fontSize: 15 }}>Create Ops Staff Account</strong>
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#6b7280' }}
              onClick={() => { setOpen(false); setResult(null); setError(null); }}>✕</button>
          </div>

          {result ? (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 16 }}>
              <p style={{ margin: '0 0 6px', fontWeight: 600, color: '#166534' }}>
                ✅ Ops account created for {result.user.fullName}
              </p>
              <p style={{ margin: '0 0 4px', fontSize: 13, color: '#374151' }}>
                Email: <strong>{result.user.email}</strong>
              </p>
              <p style={{ margin: '0 0 12px', fontSize: 13, color: '#374151' }}>
                Temporary password: <code style={{ background: '#dcfce7', padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace' }}>
                  {result.temporaryPassword}
                </code>
              </p>
              <p style={{ margin: '0 0 12px', fontSize: 12, color: '#6b7280' }}>
                Share these credentials securely. The user should change their password after first login.
              </p>
              <button style={styles.btnSecondary} onClick={dismiss}>Done</button>
            </div>
          ) : (
            <form onSubmit={submit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {error && (
                <div style={{ gridColumn: '1 / -1', background: '#fef2f2', border: '1px solid #fecaca',
                  borderRadius: 6, padding: '8px 12px', color: '#991b1b', fontSize: 13 }}>
                  {error}
                </div>
              )}
              <div>
                <label style={styles.label}>First Name *</label>
                <input style={styles.input} required value={form.firstName}
                  onChange={e => set('firstName', e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Last Name *</label>
                <input style={styles.input} required value={form.lastName}
                  onChange={e => set('lastName', e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Email *</label>
                <input style={styles.input} type="email" required value={form.email}
                  onChange={e => set('email', e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Phone (optional)</label>
                <input style={styles.input} type="tel" value={form.phoneNumber}
                  onChange={e => set('phoneNumber', e.target.value)} />
              </div>
              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" style={styles.btnSecondary}
                  onClick={() => { setOpen(false); setError(null); }}>Cancel</button>
                <button type="submit" style={styles.btnPrimary} disabled={saving}>
                  {saving ? 'Creating…' : 'Create Ops Account'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

// ── Users Tab ─────────────────────────────────────────────────────────
function UsersTab() {
  const api = useAdminApi();
  const [users, setUsers]         = useState<UserSummary[]>([]);
  const [query, setQuery]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [limitUserId, setLimitUserId] = useState<string | null>(null);
  const [limitValue, setLimitValue]   = useState('');
  const [savingLimit, setSavingLimit] = useState(false);

  // Every query change (typing, Clear+Fill, the Search button) triggers its own
  // fetch via the effect below. Responses can resolve out of order, so a stale
  // request — e.g. the empty-query "all users" fetch fired by clearing the box —
  // can land after a more specific one and overwrite it with the wrong list.
  // requestIdRef lets us drop any response that isn't from the latest request.
  const requestIdRef = useRef(0);

  const fetchUsers = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const res = await api.get<UserSummary[]>('/api/v1/users', {
        params: { query: query || undefined, page: 1, pageSize: 50 }
      });
      if (requestId === requestIdRef.current) setUsers(res.data);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [api, query]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const suspend = async (userId: string) => {
    const reason = prompt('Suspension reason:');
    if (!reason) return;
    await api.post(`/api/v1/users/${userId}/suspend`, { reason });
    setActionMsg(`User ${userId} suspended.`);
    fetchUsers();
  };

  const enable = async (userId: string) => {
    await api.post(`/api/v1/users/${userId}/enable`, {});
    setActionMsg(`User ${userId} re-enabled.`);
    fetchUsers();
  };

  const approveVerification = async (userId: string, roleName: string, name: string) => {
    if (!confirm(`Manually approve verification for ${name}? This bypasses email verification and activates their account.`)) return;
    try {
      await api.post(`/api/v1/users/${userId}/verify/approve?type=${roleName.toLowerCase()}`, { notes: 'Manual approval by admin (email bypass)' });
      setActionMsg(`✅ ${name} has been approved and their account activated.`);
      fetchUsers();
    } catch {
      setActionMsg(`Failed to approve ${name}. Please try again.`);
    }
  };

  const unlockDepositRefund = async (userId: string, name: string) => {
    if (!confirm(`Unlock deposit refund for ${name}? This allows them to request a refund of their £500 deposit.`)) return;
    try {
      await api.post(`/api/v1/eligibility/admin/${userId}/unlock-deposit-refund`, {});
      setActionMsg(`✅ Deposit refund unlocked for ${name}.`);
    } catch (err: any) {
      setActionMsg(err?.response?.data?.message ?? `Failed to unlock refund for ${name}.`);
    }
  };

  const saveLimit = async (userId: string, name: string) => {
    const n = parseInt(limitValue, 10);
    if (isNaN(n) || n < 0 || n > 200) { setActionMsg('Limit must be between 0 and 200.'); return; }
    setSavingLimit(true);
    try {
      await api.post(`/api/referrals/admin/${userId}/supplier-limit`, { limit: n });
      setActionMsg(`✅ Supplier referral limit for ${name} set to ${n}.`);
      setLimitUserId(null);
      setLimitValue('');
    } catch (err: any) {
      setActionMsg(err?.response?.data?.message ?? 'Failed to update limit.');
    } finally { setSavingLimit(false); }
  };

  return (
    <div>
      <CreateOpsPanel onCreated={fetchUsers} />
      <div style={styles.toolbar}>
        <input style={styles.searchInput} value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search by name or email…" />
        <button style={styles.btnSecondary} onClick={fetchUsers}>Search</button>
      </div>
      {actionMsg && <div style={styles.successMsg}>{actionMsg}</div>}
      {loading ? <p style={styles.loadingText}>Loading…</p> : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHead}>
                {['Name', 'Email', 'Role', 'Status', 'Email Verified', 'Registered', 'Actions'].map(h =>
                  <th key={h} style={styles.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <React.Fragment key={u.userId}>
                  <tr style={styles.tr}>
                    <td style={styles.td}>{u.fullName}</td>
                    <td style={styles.td}><span style={styles.emailText}>{u.email}</span></td>
                    <td style={styles.td}><span style={styles.roleBadge}>{u.roleName}</span></td>
                    <td style={styles.td}>
                      <span style={{ ...styles.statusBadge, ...(statusColor(u.registrationStatus)) }}>
                        {u.registrationStatus}
                      </span>
                    </td>
                    <td style={styles.td}>{u.isEmailVerified ? '✅' : '❌'}</td>
                    <td style={styles.td}>{new Date(u.createdAt).toLocaleDateString()}</td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <button style={styles.actionBtn} onClick={() => setQuery(u.email)}>View</button>
                        {(u.registrationStatus === 'PendingVerification' || u.registrationStatus === 'ProfileIncomplete') && (
                          <button
                            style={{ ...styles.actionBtn, background: '#d1fae5', color: '#065f46', fontWeight: 600 }}
                            onClick={() => approveVerification(u.userId, u.roleName, u.fullName || u.email)}
                            title="Manually approve — bypasses email verification">
                            ✓ Approve
                          </button>
                        )}
                        {(u.roleName === 'Carrier' || u.roleName === 'Workshop') && (
                          <button
                            style={{ ...styles.actionBtn, background: '#ede9fe', color: '#6d28d9' }}
                            title="Set supplier referral limit"
                            onClick={() => { setLimitUserId(limitUserId === u.userId ? null : u.userId); setLimitValue(''); }}>
                            📊 Ref. Limit
                          </button>
                        )}
                        {(u.roleName === 'Carrier' || u.roleName === 'Workshop') && (
                          <button
                            style={{ ...styles.actionBtn, background: '#fef3c7', color: '#92400e' }}
                            title="Unlock deposit refund eligibility"
                            onClick={() => unlockDepositRefund(u.userId, u.fullName || u.email)}>
                            🔓 Unlock Refund
                          </button>
                        )}
                        {u.registrationStatus === 'Suspended'
                          ? <button style={{ ...styles.actionBtn, background: '#dcfce7', color: '#166534' }}
                              onClick={() => enable(u.userId)}>Enable</button>
                          : <button style={{ ...styles.actionBtn, background: '#fee2e2', color: '#dc2626' }}
                              onClick={() => suspend(u.userId)}>Suspend</button>
                        }
                      </div>
                    </td>
                  </tr>
                  {limitUserId === u.userId && (
                    <tr>
                      <td colSpan={7} style={{ padding: '0 12px 10px', background: '#f5f3ff' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 8 }}>
                          <span style={{ fontSize: 13, color: '#6d28d9', fontWeight: 600 }}>
                            Supplier referral limit for {u.fullName || u.email}:
                          </span>
                          <input
                            type="number" min={0} max={200}
                            value={limitValue}
                            onChange={e => setLimitValue(e.target.value)}
                            placeholder="e.g. 15"
                            style={{ width: 80, border: '1px solid #c4b5fd', borderRadius: 6, padding: '4px 8px', fontSize: 13 }}
                          />
                          <button
                            onClick={() => saveLimit(u.userId, u.fullName || u.email)}
                            disabled={savingLimit || !limitValue}
                            style={{ padding: '5px 14px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: 'pointer', opacity: savingLimit ? 0.6 : 1 }}>
                            {savingLimit ? '…' : 'Save'}
                          </button>
                          <button
                            onClick={() => setLimitUserId(null)}
                            style={{ padding: '5px 10px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                            Cancel
                          </button>
                          <span style={{ fontSize: 11, color: '#9ca3af' }}>Default is 10. Range: 0–200.</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Deleted Accounts Tab ──────────────────────────────────────────────
interface DeletedUserSummary {
  userId: string; email: string; fullName: string; roleName: string;
  registrationStatus: string; deletedAt: string | null; createdAt: string;
}

function DeletedAccountsTab() {
  const api = useAdminApi();
  const [users, setUsers]     = useState<DeletedUserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]         = useState('');

  const fetchDeleted = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<DeletedUserSummary[]>('/api/admin/deleted-accounts', { params: { page: 1, pageSize: 50 } });
      setUsers(res.data);
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchDeleted(); }, [fetchDeleted]);

  const restore = async (u: DeletedUserSummary) => {
    if (!confirm(`Restore ${u.fullName || u.email}? They will be able to log in again.`)) return;
    try {
      await api.post(`/api/admin/deleted-accounts/${u.userId}/restore`, {});
      setMsg(`✅ ${u.fullName || u.email} restored.`);
      fetchDeleted();
    } catch (err: any) {
      setMsg(err?.response?.data?.message ?? `Failed to restore ${u.email}.`);
    }
  };

  const hardDelete = async (u: DeletedUserSummary) => {
    if (!confirm(
      `Permanently delete ${u.fullName || u.email}? This cannot be undone — their identity will be ` +
      `removed and their email will become available for a new registration.`
    )) return;
    try {
      await api.delete(`/api/admin/deleted-accounts/${u.userId}`);
      setMsg(`✅ ${u.fullName || u.email} permanently deleted.`);
      fetchDeleted();
    } catch (err: any) {
      setMsg(err?.response?.data?.message ?? `Failed to permanently delete ${u.email}.`);
    }
  };

  return (
    <div>
      {msg && <div style={styles.successMsg}>{msg}</div>}
      {loading ? <p style={styles.loadingText}>Loading…</p> : users.length === 0 ? (
        <p style={styles.loadingText}>No deleted accounts.</p>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHead}>
                {['Name', 'Email', 'Role', 'Deleted', 'Actions'].map(h =>
                  <th key={h} style={styles.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.userId} style={styles.tr}>
                  <td style={styles.td}>{u.fullName}</td>
                  <td style={styles.td}><span style={styles.emailText}>{u.email}</span></td>
                  <td style={styles.td}><span style={styles.roleBadge}>{u.roleName}</span></td>
                  <td style={styles.td}>{u.deletedAt ? new Date(u.deletedAt).toLocaleDateString() : '—'}</td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <button style={{ ...styles.actionBtn, background: '#dcfce7', color: '#166534' }}
                        onClick={() => restore(u)}>Restore</button>
                      <button style={{ ...styles.actionBtn, background: '#fee2e2', color: '#dc2626' }}
                        onClick={() => hardDelete(u)}>Delete Permanently</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Verification Queue Tab ────────────────────────────────────────────
function VerificationTab() {
  const api = useAdminApi();
  const [pending, setPending] = useState<PendingVerification[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]         = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.get<PendingVerification[]>('/api/v1/users/verification/pending');
    setPending(res.data);
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const approve = async (userId: string, type: string) => {
    await api.post(`/api/v1/users/${userId}/verify/approve?type=${type}`, { notes: 'Approved by admin' });
    setMsg(`Approved ${userId}`);
    load();
  };

  const reject = async (userId: string, type: string) => {
    const reason = prompt('Rejection reason:');
    if (!reason) return;
    await api.post(`/api/v1/users/${userId}/verify/reject?type=${type}`, { reason });
    setMsg(`Rejected ${userId}: ${reason}`);
    load();
  };

  return (
    <div>
      {msg && <div style={styles.successMsg}>{msg}</div>}

      {/* Pending User Verifications */}
      <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 12 }}>⏳ Pending Account Verifications</h3>
      {loading ? <p style={styles.loadingText}>Loading…</p> : pending.length === 0
        ? <p style={styles.emptyText}>No pending verifications. ✅</p>
        : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHead}>
                  {['Name', 'Role', 'Email', 'Ver. Status', 'Docs', 'Registered', 'Actions'].map(h =>
                    <th key={h} style={styles.th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {pending.map(p => (
                  <tr key={p.userId} style={styles.tr}>
                    <td style={styles.td}><strong>{p.fullName}</strong></td>
                    <td style={styles.td}><span style={styles.roleBadge}>{p.roleName}</span></td>
                    <td style={styles.td}><span style={styles.emailText}>{p.email}</span></td>
                    <td style={styles.td}>
                      {p.carrierVerStatus && <span style={styles.verBadge}>Carrier: {p.carrierVerStatus}</span>}
                      {(p.workshopVerStatus ?? p.garageVerStatus) && (
                        <span style={{ ...styles.verBadge, marginLeft: 4 }}>
                          Workshop: {p.workshopVerStatus ?? p.garageVerStatus}
                        </span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {p.pendingDocuments > 0
                        ? <span style={styles.docsBadge}>📄 {p.pendingDocuments}</span>
                        : <span style={{ color: '#9ca3af' }}>—</span>}
                    </td>
                    <td style={{ ...styles.td, fontSize: 12, color: '#6b7280' }}>
                      {new Date(p.createdAt).toLocaleDateString()}
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button title="Approve" style={{ ...styles.actionBtn, background: '#dcfce7', color: '#166534', fontWeight: 700 }}
                          onClick={() => approve(p.userId, p.roleName.toLowerCase())}>✓</button>
                        <button title="Reject" style={{ ...styles.actionBtn, background: '#fee2e2', color: '#dc2626', fontWeight: 700 }}
                          onClick={() => reject(p.userId, p.roleName.toLowerCase())}>✗</button>
                        <button style={styles.actionBtn}
                          onClick={() => window.open(`/admin/users/${p.userId}`, '_blank')}>View</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {/* DVLA Licence Verification */}
      <div style={{ marginTop: 32 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
          🪪 DVLA Licence Verification
        </h3>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
          Drivers who have submitted a share code. Open GOV.UK with the code, download the result PDF,
          upload it here to parse their licence details and set status to Verified.
        </p>
        <DvlaQueue />
      </div>

      {/* Compliance Documents */}
      <div style={{ marginTop: 32, borderTop: '1px solid #e5e7eb', paddingTop: 8 }}>
        <AdminComplianceDocumentsPage />
      </div>
    </div>
  );
}

// ── Deposit Queue ─────────────────────────────────────────────────────
interface PendingDeposit {
  userId: string; fullName: string; email: string;
  amount: number; stripeSessionId: string | null; createdAt: string;
}

function DepositQueue() {
  const api = useAdminApi();
  const [items, setItems]     = useState<PendingDeposit[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Record<string, boolean>>({});
  const [errors, setErrors]   = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.get<PendingDeposit[]>('/api/v1/eligibility/admin/pending-deposits');
      setItems(res.data);
    } catch (err: any) {
      setLoadError(err?.response?.data?.error ?? err?.message ?? 'Failed to load pending deposits.');
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const confirm = async (userId: string) => {
    if (!window.confirm('Confirm this deposit? This will mark the carrier or workshop as eligible via Route 3.')) return;
    setConfirming(c => ({ ...c, [userId]: true }));
    setErrors(e => ({ ...e, [userId]: '' }));
    try {
      await api.post(`/api/v1/eligibility/admin/${userId}/security-deposit`);
      setItems(prev => prev.filter(i => i.userId !== userId));
    } catch (err: any) {
      setErrors(e => ({ ...e, [userId]: err?.response?.data?.error ?? 'Failed to confirm.' }));
    } finally {
      setConfirming(c => ({ ...c, [userId]: false }));
    }
  };

  if (loading) return <p style={styles.loadingText}>Loading deposits…</p>;
  if (loadError) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#dc2626', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px' }}>
      ⚠️ {loadError}
      <button onClick={load} style={{ marginLeft: 8, fontSize: 12, color: '#1d4ed8', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
    </div>
  );
  if (items.length === 0) return <p style={styles.emptyText}>No pending deposits. ✅</p>;

  return (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr style={styles.tableHead}>
            {['Name', 'Email', 'Amount', 'Stripe Session', 'Date', 'Actions'].map(h =>
              <th key={h} style={styles.th}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr key={item.userId} style={styles.tr}>
              <td style={styles.td}><strong>{item.fullName}</strong></td>
              <td style={styles.td}><span style={styles.emailText}>{item.email}</span></td>
              <td style={styles.td}><strong>£{item.amount}</strong></td>
              <td style={{ ...styles.td, fontSize: 11, fontFamily: 'monospace', color: '#9ca3af' }}>
                {item.stripeSessionId ?? '—'}
              </td>
              <td style={{ ...styles.td, fontSize: 12, color: '#6b7280' }}>
                {new Date(item.createdAt).toLocaleDateString('en-GB')}
              </td>
              <td style={styles.td}>
                {errors[item.userId] && (
                  <div style={{ fontSize: 11, color: '#dc2626', marginBottom: 4 }}>{errors[item.userId]}</div>
                )}
                <button onClick={() => confirm(item.userId)} disabled={confirming[item.userId]}
                  style={{ padding: '5px 12px', fontSize: 12, background: '#10b981', color: '#fff',
                    border: 'none', borderRadius: 6, cursor: 'pointer', opacity: confirming[item.userId] ? 0.6 : 1 }}>
                  {confirming[item.userId] ? '…' : '✓ Confirm'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── DVLA Queue ────────────────────────────────────────────────────────
function DvlaQueue() {
  const api = useAdminApi();
  const [items, setItems]   = useState<DvlaPendingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState<Record<string, DvlaProcessResult>>({});
  const [errors, setErrors]   = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const fileRefs = React.useRef<Record<string, HTMLInputElement | null>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<DvlaPendingItem[]>('/api/v1/dvla/admin/pending');
      setItems(res.data);
    } catch {
      // empty queue or error — show nothing
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleFileUpload = async (userId: string, file: File) => {
    setUploading(u => ({ ...u, [userId]: true }));
    setErrors(e => ({ ...e, [userId]: '' }));
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post<DvlaProcessResult>(`/api/v1/dvla/admin/${userId}/process`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResults(r => ({ ...r, [userId]: res.data }));
      // Remove from pending list if parse succeeded
      if (res.data.parseSuccess) setItems(prev => prev.filter(i => i.userId !== userId));
    } catch (err: any) {
      setErrors(e => ({ ...e, [userId]: err?.response?.data?.error ?? 'Upload failed.' }));
    } finally {
      setUploading(u => ({ ...u, [userId]: false }));
    }
  };

  if (loading) return <p style={styles.loadingText}>Loading DVLA queue…</p>;
  if (items.length === 0 && Object.keys(results).length === 0)
    return <p style={styles.emptyText}>No pending DVLA share codes. ✅</p>;

  return (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr style={styles.tableHead}>
            {['Name', 'Email', 'Share Code', 'Submitted', 'Actions'].map(h =>
              <th key={h} style={styles.th}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr key={item.userId} style={styles.tr}>
              <td style={styles.td}><strong>{item.carrierFullName}</strong></td>
              <td style={styles.td}><span style={styles.emailText}>{item.email}</span></td>
              <td style={styles.td}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700, letterSpacing: 2, background: '#f3f4f6', padding: '2px 8px', borderRadius: 4 }}>
                    {item.shareCode}
                  </span>
                  <a href={`https://www.viewdrivingrecord.service.gov.uk/check-licence-information?shareCode=${item.shareCode}`}
                    target="_blank" rel="noreferrer"
                    style={{ fontSize: 11, color: '#047857', textDecoration: 'underline' }}>
                    GOV.UK ↗
                  </a>
                </div>
              </td>
              <td style={{ ...styles.td, fontSize: 12, color: '#6b7280' }}>
                {new Date(item.submittedAt).toLocaleDateString('en-GB')}
              </td>
              <td style={styles.td}>
                <input type="file" accept=".pdf,application/pdf" style={{ display: 'none' }}
                  ref={el => { fileRefs.current[item.userId] = el; }}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(item.userId, file);
                    e.target.value = '';
                  }}
                />
                {errors[item.userId] && (
                  <div style={{ fontSize: 11, color: '#dc2626', marginBottom: 4 }}>{errors[item.userId]}</div>
                )}
                <button style={{ ...styles.actionBtn, background: '#ecfdf5', color: '#065f46' }}
                  disabled={uploading[item.userId]}
                  onClick={() => fileRefs.current[item.userId]?.click()}>
                  {uploading[item.userId] ? '⏳ Processing…' : '📄 Upload PDF'}
                </button>
              </td>
            </tr>
          ))}
          {Object.entries(results).map(([userId, r]) => (
            <tr key={`result-${userId}`} style={{ ...styles.tr, background: r.parseSuccess ? '#f0fdf4' : '#fffbeb' }}>
              <td style={styles.td} colSpan={2}>
                <span style={{ fontWeight: 700, color: r.parseSuccess ? '#166534' : '#92400e' }}>
                  {r.parseSuccess ? '✓ Verified' : '⚠ Parse failed'}
                </span>
                {r.parseSuccess && <span style={{ fontSize: 13, color: '#374151', marginLeft: 8 }}>{r.carrierFullName}</span>}
              </td>
              <td style={styles.td} colSpan={3}>
                {r.parseSuccess
                  ? <span style={{ fontSize: 12, color: '#6b7280' }}>{r.licenceNumber} · {r.drivingStatus} · Expires: {r.licenceValidTo ?? '—'} · Points: {r.endorsementPoints}</span>
                  : <span style={{ fontSize: 12, color: '#92400e' }}>{r.parseFailureReason ?? 'Could not read the PDF. Re-upload the correct GOV.UK PDF.'}</span>
                }
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Payments Tab ──────────────────────────────────────────────────────
function PaymentsTab() {
  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
          💳 Outgoing Payment Verification
        </h3>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
          Review POD evidence and release driver payments. Ops approve → Admin release.
        </p>
        <PaymentVerificationQueuePage />
      </div>
      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 24 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
          🔐 Security Deposits
        </h3>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
          Carriers who completed Stripe checkout but whose deposit has not been confirmed.
          Confirm manually once payment is visible in Stripe.
        </p>
        <DepositQueue />
      </div>
    </div>
  );
}

// ── External Ratings Tab ─────────────────────────────────────────────
function ExternalRatingsTab() {
  const api = useAdminApi();
  const [items, setItems]           = useState<PendingScreenshot[]>([]);
  const [loading, setLoading]       = useState(true);
  const [msg, setMsg]               = useState('');
  const [processing, setProcessing] = useState<string | null>(null);
  const [ratings, setRatings]       = useState<Record<string, number>>({});
  const [refTexts, setRefTexts]     = useState<Record<string, string>>({});
  const [reasons, setReasons]       = useState<Record<string, string>>({});
  const [expanded, setExpanded]     = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<PendingScreenshot[]>('/api/v1/ratings/screenshots/admin/pending');
      setItems(res.data);
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id: string) => {
    const rating = ratings[id];
    const text   = refTexts[id]?.trim();
    if (!rating || rating < 1 || rating > 5) { setMsg('Enter a rating between 1.0 and 5.0.'); return; }
    if (!text) { setMsg('Enter reference text before approving.'); return; }
    setProcessing(id);
    try {
      await api.post(`/api/v1/ratings/screenshots/admin/${id}/approve`, {
        approvedRating: rating, approvedReferenceText: text,
      });
      setMsg('Screenshot approved and published.');
      setItems(prev => prev.filter(i => i.uploadId !== id));
      setExpanded(null);
    } catch { setMsg('Approval failed.'); }
    finally { setProcessing(null); }
  };

  const reject = async (id: string) => {
    const reason = reasons[id]?.trim();
    if (!reason) { setMsg('Enter a rejection reason first.'); return; }
    setProcessing(id);
    try {
      await api.post(`/api/v1/ratings/screenshots/admin/${id}/reject`, { rejectionReason: reason });
      setMsg('Screenshot rejected.');
      setItems(prev => prev.filter(i => i.uploadId !== id));
      setExpanded(null);
    } catch { setMsg('Rejection failed.'); }
    finally { setProcessing(null); }
  };

  return (
    <div>
      <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
        Review screenshot uploads from drivers and workshops. Set the approved rating and reference
        text, then approve or reject.
      </p>
      {msg && <div style={styles.successMsg}>{msg}</div>}
      {loading ? (
        <p style={styles.loadingText}>Loading…</p>
      ) : items.length === 0 ? (
        <p style={styles.emptyText}>No pending screenshot uploads. ✅</p>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHead}>
                {['Platform', 'User', 'Role', 'File', 'Uploaded', 'Actions'].map(h =>
                  <th key={h} style={styles.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <React.Fragment key={item.uploadId}>
                  <tr style={styles.tr}>
                    <td style={styles.td}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 18 }}>{platformIcon(item.platform)}</span>
                        <strong style={{ fontSize: 13 }}>{item.platform}</strong>
                      </span>
                    </td>
                    <td style={{ ...styles.td, fontSize: 13, fontWeight: 500 }}>
                      {item.fullName || '—'}
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.roleBadge, background: '#d1fae5', color: '#065f46' }}>{item.userRole}</span>
                    </td>
                    <td style={{ ...styles.td, fontSize: 12, color: '#6b7280', maxWidth: 200 }}>
                      {item.originalFileName}
                    </td>
                    <td style={{ ...styles.td, fontSize: 12, color: '#6b7280' }}>
                      {new Date(item.uploadedAt).toLocaleDateString()}
                    </td>
                    <td style={styles.td}>
                      <button style={styles.actionBtn}
                        onClick={() => setExpanded(expanded === item.uploadId ? null : item.uploadId)}>
                        {expanded === item.uploadId ? 'Hide ▲' : 'Review ▼'}
                      </button>
                    </td>
                  </tr>
                  {expanded === item.uploadId && (
                    <tr>
                      <td colSpan={6} style={{ padding: '0 16px 16px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                        {item.screenshotSasUrl ? (
                          <img src={item.screenshotSasUrl} alt="Rating screenshot"
                            style={{ width: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 8,
                                     border: '1px solid #e5e7eb', background: '#f9fafb', margin: '12px 0' }}
                          />
                        ) : (
                          <div style={{ margin: '12px 0', padding: 16, textAlign: 'center',
                                        color: '#9ca3af', fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 8 }}>
                            No screenshot preview available
                          </div>
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <div style={{ padding: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
                            <p style={{ fontSize: 11, fontWeight: 700, color: '#166534', textTransform: 'uppercase', marginBottom: 8 }}>Approve</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <label style={{ fontSize: 12, color: '#374151' }}>
                                Rating (1.0–5.0)
                                <input type="number" min={1} max={5} step={0.1}
                                  value={ratings[item.uploadId] ?? ''}
                                  onChange={e => setRatings(r => ({ ...r, [item.uploadId]: parseFloat(e.target.value) || 0 }))}
                                  style={{ marginLeft: 8, width: 65, border: '1px solid #d1d5db', borderRadius: 4, padding: '2px 6px', fontSize: 13 }}
                                />
                              </label>
                              <input type="text"
                                value={refTexts[item.uploadId] ?? ''}
                                onChange={e => setRefTexts(r => ({ ...r, [item.uploadId]: e.target.value }))}
                                placeholder={`e.g. "4.8/5 from 127 reviews on ${item.platform}"`}
                                style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 4, padding: '5px 8px', fontSize: 12, boxSizing: 'border-box' }}
                              />
                              <button onClick={() => approve(item.uploadId)} disabled={processing === item.uploadId}
                                style={{ ...styles.actionBtn, background: '#dcfce7', color: '#166534', opacity: processing === item.uploadId ? 0.5 : 1 }}>
                                ✓ Approve &amp; Publish
                              </button>
                            </div>
                          </div>
                          <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
                            <p style={{ fontSize: 11, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', marginBottom: 8 }}>Reject</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <input type="text"
                                value={reasons[item.uploadId] ?? ''}
                                onChange={e => setReasons(r => ({ ...r, [item.uploadId]: e.target.value }))}
                                placeholder="Rejection reason (required)…"
                                style={{ width: '100%', border: '1px solid #fca5a5', borderRadius: 4, padding: '5px 8px', fontSize: 12, boxSizing: 'border-box' }}
                              />
                              <button onClick={() => reject(item.uploadId)} disabled={processing === item.uploadId}
                                style={{ ...styles.actionBtn, background: '#fee2e2', color: '#dc2626', opacity: processing === item.uploadId ? 0.5 : 1 }}>
                                ✗ Reject
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Stats Tab ─────────────────────────────────────────────────────────
function StatsTab() {
  const api = useAdminApi();
  const [stats, setStats]     = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<AdminStats>('/api/v1/users/admin/stats')
      .then((r: { data: AdminStats }) => setStats(r.data))
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) return <p style={styles.loadingText}>Loading stats…</p>;
  if (!stats)  return <p style={styles.emptyText}>No stats available.</p>;

  return (
    <div>
      <div style={styles.statsGrid}>
        <AdminStatCard label="Total Users"          value={stats.totalUsers}              icon="👥" color="#065f46" bg="#d1fae5" />
        <AdminStatCard label="Pending Verifications" value={stats.pendingVerifications}    icon="⏳" color="#92400e" bg="#fef3c7" />
        <AdminStatCard label="Pending Ratings"       value={stats.pendingExternalRatings}  icon="⭐" color="#6b21a8" bg="#f3e8ff" />
        <AdminStatCard label="Active Jobs Today"     value={stats.activeJobsToday}         icon="📦" color="#065f46" bg="#d1fae5" />
        <AdminStatCard label="Jobs This Month"       value={stats.completedJobsThisMonth}  icon="✅" color="#166534" bg="#dcfce7" />
        <AdminStatCard label="Revenue This Month"    value={`£${stats.revenueThisMonth.toFixed(0)}`} icon="💰" color="#166534" bg="#dcfce7" />
        <AdminStatCard label="Open Disputes"         value={stats.openDisputes}            icon="⚖️" color="#991b1b" bg="#fee2e2" />
        <AdminStatCard label="Verified Carriers"      value={stats.registeredCarriers}       icon="🚐" color="#065f46" bg="#d1fae5" />
        <AdminStatCard label="Verified Workshops"    value={stats.registeredWorkshops ?? 0} icon="🔧" color="#065f46" bg="#d1fae5" />
      </div>
    </div>
  );
}

function AdminStatCard({ label, value, icon, color, bg }: {
  label: string; value: string | number; icon: string; color: string; bg: string;
}) {
  return (
    <div style={{ ...styles.statCard, background: bg }}>
      <span style={{ fontSize: 28 }}>{icon}</span>
      <span style={{ fontSize: 24, fontWeight: 700, color }}>{value}</span>
      <span style={{ fontSize: 12, color: '#6b7280', textAlign: 'center' }}>{label}</span>
    </div>
  );
}

// ── Context display helper ─────────────────────────────────────────────
function ContextDisplay({ raw }: { raw: string }) {
  try {
    const obj = JSON.parse(raw) as Record<string, unknown>;
    return (
      <table style={{ fontSize: 11, borderCollapse: 'collapse', width: '100%' }}>
        <tbody>
          {Object.entries(obj).map(([k, v]) => (
            <tr key={k}>
              <td style={{ padding: '2px 8px', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap',
                background: '#f1f5f9', border: '1px solid #e2e8f0', width: '28%' }}>{k}</td>
              <td style={{ padding: '2px 8px', fontFamily: 'monospace', color: '#374151',
                background: '#fff', border: '1px solid #e2e8f0', wordBreak: 'break-all' }}>
                {typeof v === 'object' ? JSON.stringify(v) : String(v)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  } catch {
    return (
      <pre style={{ fontSize: 11, background: '#f1f5f9', padding: '6px 10px', borderRadius: 4,
        margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{raw}</pre>
    );
  }
}

// ── Errors Tab ────────────────────────────────────────────────────────
function ErrorsTab() {
  const api = useAdminApi();
  const [errors, setErrors]             = useState<TechnicalError[]>([]);
  const [loading, setLoading]           = useState(true);
  const [typeFilter, setTypeFilter]     = useState('');
  const [statusFilter, setStatusFilter] = useState('New');
  const [expanded, setExpanded]         = useState<number | null>(null);
  const [showFullTrace, setShowFullTrace] = useState<number | null>(null);
  const [msg, setMsg]                   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<TechnicalError[]>('/api/v1/technical-errors/admin', {
        params: { errorType: typeFilter || undefined, status: statusFilter || undefined, page: 1, pageSize: 100 },
      });
      setErrors(res.data);
    } finally { setLoading(false); }
  }, [api, typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id: number) => {
    const notes = prompt('Resolution notes (optional):');
    if (notes === null) return;
    await api.post(`/api/v1/technical-errors/admin/${id}/resolve`, { resolutionNotes: notes || null });
    setMsg(`Error #${id} resolved.`);
    load();
  };

  const errorTypeColor = (t: string): React.CSSProperties => {
    if (t === 'Frontend') return { background: '#d1fae5', color: '#065f46' };
    if (t === 'Mobile')   return { background: '#fce7f3', color: '#9d174d' };
    return { background: '#fee2e2', color: '#991b1b' };
  };

  const topFrames = (trace: string, n = 8) =>
    trace.split('\n').filter(l => l.trim()).slice(0, n).join('\n');

  return (
    <div>
      <div style={styles.toolbar}>
        <select style={{ ...styles.searchInput, flex: 'none', width: 140 }}
          value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          <option value="Frontend">Frontend</option>
          <option value="Mobile">Mobile</option>
          <option value="Api">Api</option>
        </select>
        <select style={{ ...styles.searchInput, flex: 'none', width: 120 }}
          value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All</option>
          <option value="New">New</option>
          <option value="Resolved">Resolved</option>
        </select>
        <button style={styles.btnSecondary} onClick={load}>Refresh</button>
      </div>
      {msg && <div style={styles.successMsg}>{msg}</div>}
      {loading ? <p style={styles.loadingText}>Loading…</p> : errors.length === 0
        ? <p style={styles.emptyText}>No errors found. ✅</p>
        : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHead}>
                  {['Type', 'Message', 'Context / URL', 'User', 'When', 'Count', 'Status', 'Actions'].map(h =>
                    <th key={h} style={styles.th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {errors.map(e => (
                  <React.Fragment key={e.id}>
                    <tr style={{ ...styles.tr, borderLeft: `4px solid ${e.status === 'Resolved' ? '#22c55e' : '#ef4444'}` }}>
                      <td style={styles.td}>
                        <span style={{ ...styles.statusBadge, ...errorTypeColor(e.errorType) }}>{e.errorType}</span>
                      </td>
                      <td style={{ ...styles.td, maxWidth: 220 }}>
                        <span style={{ fontSize: 13, color: '#111827', wordBreak: 'break-word' }}>
                          {e.message.slice(0, 100)}{e.message.length > 100 ? '…' : ''}
                        </span>
                      </td>
                      <td style={{ ...styles.td, maxWidth: 200 }}>
                        {e.context && (
                          <span style={{ fontSize: 11, color: '#374151', display: 'block' }} title={e.context}>
                            {e.context.slice(0, 60)}{e.context.length > 60 ? '…' : ''}
                          </span>
                        )}
                        {e.url && (
                          <span style={{ fontSize: 11, color: '#9ca3af', display: 'block' }}>
                            {e.url.replace(/^https?:\/\/[^/]+/, '').slice(0, 50)}
                          </span>
                        )}
                      </td>
                      <td style={{ ...styles.td, fontSize: 12, color: '#6b7280' }}>{e.userEmail ?? '—'}</td>
                      <td style={{ ...styles.td, fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                        {new Date(e.createdAt).toLocaleString()}
                      </td>
                      <td style={styles.td}>
                        {e.totalCount > 1
                          ? <span style={{ ...styles.statusBadge, background: '#fef3c7', color: '#92400e' }}>{e.totalCount}×</span>
                          : <span style={{ fontSize: 12, color: '#9ca3af' }}>1</span>}
                      </td>
                      <td style={styles.td}>
                        {e.status === 'Resolved'
                          ? <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>✓ Done</span>
                          : <span style={{ ...styles.statusBadge, background: '#fee2e2', color: '#991b1b' }}>New</span>}
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button style={styles.actionBtn}
                            onClick={() => { setExpanded(expanded === e.id ? null : e.id); setShowFullTrace(null); }}>
                            {expanded === e.id ? 'Hide ▲' : 'Details ▼'}
                          </button>
                          {e.status === 'New' && (
                            <button style={{ ...styles.actionBtn, background: '#dcfce7', color: '#166534' }}
                              onClick={() => resolve(e.id)}>✓</button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded === e.id && (
                      <tr>
                        <td colSpan={8} style={{ padding: '0 16px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                          {e.context && (
                            <div style={{ marginTop: 12 }}>
                              <p style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Context</p>
                              <ContextDisplay raw={e.context} />
                            </div>
                          )}
                          {e.stackTrace && (
                            <div style={{ marginTop: 12 }}>
                              <p style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                                Stack trace {showFullTrace !== e.id && `(top ${Math.min(8, e.stackTrace.split('\n').filter(l => l.trim()).length)} frames)`}
                              </p>
                              <pre style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, padding: 10, fontSize: 11, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 220, overflowY: 'auto', color: '#374151', margin: 0 }}>
                                {showFullTrace === e.id ? e.stackTrace : topFrames(e.stackTrace)}
                              </pre>
                              {e.stackTrace.split('\n').filter(l => l.trim()).length > 8 && (
                                <button style={{ fontSize: 11, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', marginTop: 4, textDecoration: 'underline' }}
                                  onClick={() => setShowFullTrace(showFullTrace === e.id ? null : e.id)}>
                                  {showFullTrace === e.id ? 'Show less' : `Show full trace (${e.stackTrace.split('\n').filter(l => l.trim()).length} lines)`}
                                </button>
                              )}
                            </div>
                          )}
                          {e.componentStack && (
                            <div style={{ marginTop: 10 }}>
                              <p style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Component stack</p>
                              <pre style={{ background: '#fef9f9', border: '1px solid #fecaca', borderRadius: 6, padding: 10, fontSize: 11, overflowX: 'auto', whiteSpace: 'pre-wrap', maxHeight: 120, overflowY: 'auto', color: '#7f1d1d', margin: 0 }}>
                                {topFrames(e.componentStack, 5)}
                              </pre>
                            </div>
                          )}
                          <div style={{ marginTop: 8, fontSize: 11, color: '#9ca3af', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                            {e.platform   && <span>Platform: {e.platform}</span>}
                            {e.appVersion && <span>v{e.appVersion}</span>}
                            {e.userAgent  && <span title={e.userAgent}>UA: {e.userAgent.slice(0, 60)}{e.userAgent.length > 60 ? '…' : ''}</span>}
                            {e.resolutionNotes && (
                              <span style={{ color: '#22c55e' }}>Resolution: {e.resolutionNotes}</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

// ── Credit Requests Tab ───────────────────────────────────────────────
interface CreditRequest {
  userId: string;
  fullName: string;
  email: string;
  isBusinessAccount: boolean;
  businessName?: string;
  creditApprovalStatus: string;
  creditApprovedAt?: string;
  creditRejectionReason?: string;
  requestedAt?: string;
  identityVerifiedAt?: string;
}

function CreditRequestsTab() {
  const api = useAdminApi();
  const [statusFilter, setStatusFilter] = useState<string>('Pending');
  const [requests, setRequests]         = useState<CreditRequest[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [rejectUserId, setRejectUserId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const load = async (filter: string) => {
    setLoading(true); setError(null);
    try {
      const params: Record<string, string> = { page: '1', pageSize: '100' };
      if (filter !== 'All') params.status = filter;
      const res = await api.get<CreditRequest[]>('/api/v1/users/credit/requests', { params });
      setRequests(res.data);
    } catch {
      setError('Failed to load Credit requests.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(statusFilter); }, [statusFilter]);

  const approve = async (userId: string) => {
    setActionLoading(userId);
    try {
      await api.post(`/api/v1/users/${userId}/credit/approve`);
      load(statusFilter);
    } catch { setError('Approval failed.'); }
    finally { setActionLoading(null); }
  };

  const reject = async () => {
    if (!rejectUserId || !rejectReason.trim()) return;
    setActionLoading(rejectUserId);
    try {
      await api.post(`/api/v1/users/${rejectUserId}/credit/reject`, { reason: rejectReason });
      setRejectUserId(null); setRejectReason('');
      load(statusFilter);
    } catch { setError('Rejection failed.'); }
    finally { setActionLoading(null); }
  };

  const statusBadge = (s: string) => {
    const map: Record<string, { bg: string; text: string }> = {
      Pending:  { bg: '#dbeafe', text: '#1e40af' },
      Approved: { bg: '#dcfce7', text: '#166534' },
      Rejected: { bg: '#fee2e2', text: '#991b1b' },
    };
    const c = map[s] ?? { bg: '#f3f4f6', text: '#374151' };
    return (
      <span style={{ background: c.bg, color: c.text, fontSize: 11, fontWeight: 600,
        padding: '2px 8px', borderRadius: 999 }}>
        {s}
      </span>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Credit Account Requests</h2>
        <div style={{ display: 'flex', gap: 6 }}>
          {['All', 'Pending', 'Approved', 'Rejected'].map(f => (
            <button key={f}
              onClick={() => setStatusFilter(f)}
              style={{
                padding: '4px 12px', borderRadius: 6, border: '1px solid',
                borderColor: statusFilter === f ? '#3b82f6' : '#d1d5db',
                background: statusFilter === f ? '#eff6ff' : '#fff',
                color: statusFilter === f ? '#1d4ed8' : '#374151',
                fontSize: 13, fontWeight: statusFilter === f ? 600 : 400, cursor: 'pointer',
              }}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c',
          padding: '8px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
          {error}
        </div>
      )}

      {loading ? (
        <p style={styles.loadingText}>Loading…</p>
      ) : requests.length === 0 ? (
        <p style={styles.emptyText}>No Credit requests with status: {statusFilter}</p>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHead}>
                {['Name', 'Email', 'Type', 'Status', 'ID Verified', 'Requested', 'Actions'].map(h =>
                  <th key={h} style={styles.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {requests.map(r => (
                <React.Fragment key={r.userId}>
                  <tr style={styles.tr}>
                    <td style={styles.td}>
                      <strong>{r.fullName}</strong>
                      {r.isBusinessAccount && r.businessName && (
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{r.businessName}</div>
                      )}
                    </td>
                    <td style={styles.td}><span style={styles.emailText}>{r.email}</span></td>
                    <td style={styles.td}>
                      <span style={{ fontSize: 11, background: '#f3f4f6', color: '#374151', padding: '2px 8px', borderRadius: 999 }}>
                        {r.isBusinessAccount ? 'Business' : 'Individual'}
                      </span>
                    </td>
                    <td style={styles.td}>{statusBadge(r.creditApprovalStatus)}</td>
                    <td style={styles.td}>
                      {r.identityVerifiedAt
                        ? <span style={{ fontSize: 12, color: '#166534' }}>✓ {new Date(r.identityVerifiedAt).toLocaleDateString('en-GB')}</span>
                        : <span style={{ fontSize: 12, color: '#9ca3af' }}>—</span>}
                    </td>
                    <td style={{ ...styles.td, fontSize: 12, color: '#6b7280' }}>
                      {r.requestedAt ? new Date(r.requestedAt).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td style={styles.td}>
                      {r.creditApprovalStatus === 'Pending' ? (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button title="Approve" onClick={() => approve(r.userId)} disabled={actionLoading === r.userId}
                            style={{ ...styles.actionBtn, background: '#dcfce7', color: '#166534', fontWeight: 700, opacity: actionLoading === r.userId ? 0.6 : 1 }}>
                            {actionLoading === r.userId ? '…' : '✓'}
                          </button>
                          <button title="Reject" onClick={() => { setRejectUserId(r.userId); setRejectReason(''); }}
                            disabled={actionLoading === r.userId}
                            style={{ ...styles.actionBtn, background: '#fee2e2', color: '#dc2626', fontWeight: 700, opacity: actionLoading === r.userId ? 0.6 : 1 }}>
                            ✗
                          </button>
                        </div>
                      ) : (
                        <div>
                          {r.creditRejectionReason && (
                            <span style={{ fontSize: 11, color: '#991b1b' }} title={r.creditRejectionReason}>
                              {r.creditRejectionReason.slice(0, 30)}{r.creditRejectionReason.length > 30 ? '…' : ''}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  {rejectUserId === r.userId && (
                    <tr>
                      <td colSpan={7} style={{ padding: '0 12px 12px', background: '#fef2f2' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', paddingTop: 8 }}>
                          <textarea rows={2} value={rejectReason}
                            onChange={e => setRejectReason(e.target.value)}
                            placeholder="Rejection reason (required)…"
                            style={{ flex: 1, borderRadius: 6, border: '1px solid #fca5a5', padding: '6px 8px', fontSize: 13, resize: 'vertical' }}
                          />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <button onClick={reject} disabled={!rejectReason.trim() || actionLoading === r.userId}
                              style={{ padding: '6px 14px', borderRadius: 6, background: '#dc2626', color: '#fff',
                                border: 'none', fontWeight: 600, fontSize: 12, cursor: 'pointer',
                                opacity: (!rejectReason.trim() || actionLoading === r.userId) ? 0.6 : 1 }}>
                              Confirm
                            </button>
                            <button onClick={() => { setRejectUserId(null); setRejectReason(''); }}
                              style={{ padding: '6px 14px', borderRadius: 6, background: '#f3f4f6', color: '#374151',
                                border: '1px solid #d1d5db', fontSize: 12, cursor: 'pointer' }}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────
function tabLabel(t: Tab): string {
  return {
    users:        '👥 Users',
    deleted:      '🗑 Deleted Accounts',
    verification: '✅ Verification & Docs',
    payments:     '💰 Payments',
    ratings:      '⭐ External Ratings',
    mailbox:      '✉ Mailbox',
    credit:       '💳 Credit Requests',
    stats:        '📊 Stats',
    errors:       '🔴 Errors',
  }[t] ?? t;
}

function platformIcon(p: string): string {
  return { Google: '🔵', Trustpilot: '⭐', Checkatrade: '🔧' }[p] ?? '🌐';
}

function statusColor(s: string): React.CSSProperties {
  const map: Record<string, React.CSSProperties> = {
    Verified:            { background: '#dcfce7', color: '#166534' },
    PendingVerification: { background: '#fef9c3', color: '#854d0e' },
    Suspended:           { background: '#fee2e2', color: '#991b1b' },
    ProfileIncomplete:   { background: '#f3f4f6', color: '#6b7280' },
  };
  return map[s] ?? { background: '#f3f4f6', color: '#6b7280' };
}

// ── Styles ────────────────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  page:         { width: '100%', padding: '24px 16px' },
  header:       { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 },
  h1:           { fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 },
  badge:        { fontSize: 12, padding: '3px 10px', background: '#ecfdf5', color: '#065f46', borderRadius: 12, border: '1px solid #a7f3d0' },
  tabs:         { display: 'flex', gap: 4, borderBottom: '2px solid #e5e7eb', marginBottom: 20 },
  tab:          { padding: '10px 18px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#6b7280', borderBottom: '2px solid transparent', marginBottom: -2 },
  tabActive:    { color: '#047857', borderBottom: '2px solid #047857', fontWeight: 700 },
  content:      { minHeight: 400 },
  toolbar:      { display: 'flex', gap: 8, marginBottom: 16 },
  searchInput:  { flex: 1, padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 },
  tableWrap:    { overflowX: 'auto' },
  table:        { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  tableHead:    { background: '#f9fafb' },
  th:           { padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' },
  tr:           { borderBottom: '1px solid #f3f4f6' },
  td:           { padding: '10px 12px', verticalAlign: 'middle' },
  emailText:    { color: '#047857', fontSize: 12 },
  roleBadge:    { fontSize: 11, padding: '2px 8px', background: '#d1fae5', color: '#065f46', borderRadius: 12 },
  statusBadge:  { fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 500 },
  actionBtn:    { padding: '5px 10px', fontSize: 12, border: 'none', borderRadius: 6, cursor: 'pointer', background: '#f3f4f6', color: '#374151', whiteSpace: 'nowrap' },
  verCard:      { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  verCardTop:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 },
  docsBadge:    { fontSize: 12, padding: '3px 10px', background: '#fef3c7', color: '#92400e', borderRadius: 12 },
  verBadge:     { fontSize: 12, padding: '3px 10px', background: '#ecfdf5', color: '#065f46', borderRadius: 12 },
  successMsg:   { background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 14px', color: '#166534', fontSize: 13, marginBottom: 12 },
  loadingText:  { color: '#6b7280', fontSize: 13 },
  emptyText:    { color: '#9ca3af', fontSize: 13 },
  statsGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 },
  statCard:     { borderRadius: 10, padding: '16px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  btnSecondary: { padding: '9px 16px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, cursor: 'pointer' },
  btnPrimary:   { padding: '9px 16px', background: '#047857', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontWeight: 600 },
  label:        { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 },
  input:        { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' as const },
};
